import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Typography } from "@mui/material";
import CODE from "../../../Static/Constants/StatusCodes";
import { getSOHDegradation, getSohRUL } from "../../../Api/SpecificBatteryApi";
import BatteryHealthChart from "../Tabs/BatteryHealth/BatteryHealthChart";
import { PrimaryGranularityButton } from "../../../Theme/DigitalTwinStyles";
import { SetBatteryHealthCycleAge } from "../../../Actions/BatteryHealth";



const BatteryHealthDegradationChartSection = ({
    pagesContent,
    pageLabel = "",
    searchID,
    searchColumn,
    deviceID
}) => {

    const dispatch = useDispatch()
    const { t } = useTranslation([`health`])
    const cycleAge = useSelector((state) => state.BatteryHealthCycleAge.value)
    const cycleAgeTypeText = pagesContent?.translations?.eqCycle || "Eq. Cycle"
    const [chartType, setChartType] = useState('age')

    const [sohDegradationChartData, setSohDegradationChartData] = useState({
        sohData: [],
        rulData: [],
        responseStatus: { code: CODE.LOADING, msg: "" },
    });

    const fetchDegradationChartData = async () => {
        setSohDegradationChartData({
            sohData: [],
            rulData: [],
            responseStatus: { code: CODE.LOADING, msg: "" },
        });
        const degradationData = await fetchDegradationData()
        const rulData = await fetchRULData()

        if ([degradationData.responseStatus.code, rulData.responseStatus.code].includes(CODE.SESSION_EXPIRED)) {
            setSohDegradationChartData({
                sohData: [],
                rulData: [],
                responseStatus: { code: CODE.SESSION_EXPIRED, msg: "" },
            });
            return
        }

        const sohData = degradationData?.data || [];
        setSohDegradationChartData({
            sohData,
            rulData: rulData?.data || [],
            sohActualResponse: degradationData?.sohActualResponse || [],
            rulActualResponse: rulData?.rulActualResponse || [],
            responseStatus: { code: degradationData.responseStatus.code, msg: "" },
        });
    }

    const fetchDegradationData = async () => {
        const res = await getSOHDegradation(searchColumn, searchID)
        const { responseStatus, response } = res;
        if (responseStatus.code !== CODE.SUCCESS) {
            return {
                sohData: [],
                sohActualResponse: [],
                responseStatus: {
                    code: responseStatus.code,
                    msg: responseStatus.msg,
                }
            }
        }

        const batteryData = response.data;
        const hasValidData = batteryData.some((item) => item.sohEst !== null);

        if (!hasValidData) {
            return {
                sohData: [],
                sohActualResponse: [],
                responseStatus: {
                    code: 400,
                    msg: "No Data Found",
                }
            }
        }

        const processBatteryData = (item) => {
            if (item.sohEst !== null) {
                return {
                    x: cycleAge ? (item.eqCycleEst ?? 0) : (item.age ?? 0) / 30,
                    y: item.sohEst,
                    secondaryX: cycleAge ? (item.age ?? 0) / 30 : (item.eqCycleEst ?? 0),
                    date: item.date,
                    presentCapacity: parseFloat(item?.presentCapacity ?? 0),
                    sohConfidence: item.sohConfidence ?? null
                };
            }
            return null;
        };

        const chartData = batteryData
            .map(processBatteryData)
            .filter(
                (value, index, self) => {
                    return value !== null && index ===
                        self.findIndex((t) => t !== null && t.x === value.x && t.y === value.y)
                }
            );

        return {
            data: chartData,
            sohActualResponse: batteryData,
            responseStatus: { code: responseStatus.code, msg: "" },
        }
    };

    const fetchRULData = async () => {
        const res = await getSohRUL(searchColumn, searchID)
        const { responseStatus, response } = res;
        if (responseStatus.code !== CODE.SUCCESS) {
            return {
                data: [],
                responseStatus: {
                    code: responseStatus.code,
                    msg: responseStatus.msg,
                }
            }
        }

        const rulData = response.data;
        const hasValidData = rulData.some((item) => item.sohForecast !== null);

        if (!hasValidData) {
            return {
                data: [],
                responseStatus: {
                    code: 400,
                    msg: "No Data Found",
                }
            }
        }

        const processBatteryData = (item) => {
            if (item.sohForecast !== null) {
                return {
                    x: cycleAge ? (item.eqCycleEst ?? 0) : (item.calendarAgeInDays ?? 0) / 30,
                    y: item.sohForecast,
                    secondaryX: cycleAge ? (item.calendarAgeInDays ?? 0) / 30 : (item.eqCycleEst ?? 0),
                    date: item.date,
                    presentCapacity: parseFloat(item?.presentCapacity ?? 0)
                };
            }
            return null;
        };

        const chartData = rulData
            .map(processBatteryData)
            .filter(
                (value, index, self) =>
                    value !== null &&
                    index === self.findIndex((t) => t !== null && t.x === value.x && t.y === value.y)
            );

        return {
            data: chartData,
            rulActualResponse: rulData,
            responseStatus: { code: responseStatus.code, msg: "" },
        }
    };

    const updateChartType = (type) => {

        if (type === chartType) {
            return
        }

        if (type === 'age') {
            dispatch(SetBatteryHealthCycleAge(false))
        } else {
            dispatch(SetBatteryHealthCycleAge(true))
        }

        setChartType(type)
    }

    useEffect(() => {
        fetchDegradationChartData()
    }, [searchID, searchColumn, cycleAge, chartType]);


    return (
        <Box sx={{ marginTop: 'auto' }}>
            <BatteryHealthChart
                data={sohDegradationChartData}
                cycleAgeTypeText={cycleAgeTypeText}
                cycleAge={cycleAge}
                yTitleColor={"#6D72F6"}
                searchID={searchID}
                deviceID={deviceID}
                searchColumn={searchColumn}
                selectedXAxisTitle={chartType === 'age' ? 'Age (months)' : cycleAgeTypeText}
            />
            <Grid2 container justifyContent={'center'} sx={{ marginTop: '-15px' }}>
                <Grid2 container sx={{ background: '#F9F9F9', padding: '4px', borderRadius: '6px' }}>
                    <PrimaryGranularityButton
                        disabled={false}
                        onClick={() => { updateChartType('age') }}
                        active={chartType === 'age'}>
                        <Typography textTransform="capitalize">Age (months)</Typography>
                    </PrimaryGranularityButton>

                    <PrimaryGranularityButton
                        disabled={false}
                        onClick={() => { updateChartType('bms') }}
                        active={chartType === 'bms'}>
                        <Typography textTransform="capitalize">{cycleAgeTypeText}</Typography>
                    </PrimaryGranularityButton>
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default BatteryHealthDegradationChartSection;
